import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Loader2, Edit2, X, Car, User, Fuel, Calendar } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { DatePickerFr } from "../../ui/date-picker-fr";
import {
  FUEL_TYPE_OPTIONS,
  VEHICLE_CATEGORY_OPTIONS,
  ACQUISITION_TYPE_OPTIONS,
  VEHICLE_STATUS_OPTIONS,
  FUEL_TYPE_LABELS,
  VEHICLE_CATEGORY_LABELS,
} from "../../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function GeneralInfoTab({ vehicle, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({});
  
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (vehicle) {
      setForm({
        brand: vehicle.brand || "",
        model: vehicle.model || "",
        trim: vehicle.trim || "",
        year: vehicle.year || "",
        registration: vehicle.registration || "",
        vin: vehicle.vin || "",
        first_registration_date: vehicle.first_registration_date || "",
        fuel_type: vehicle.fuel_type || "",
        fiscal_power: vehicle.fiscal_power || "",
        real_power: vehicle.real_power || "",
        color: vehicle.color || "",
        category: vehicle.category || "",
        driver_employee_id: vehicle.driver_employee_id || "",
        assignment: vehicle.assignment || "",
        status: vehicle.status || "active",
        acquisition_type: vehicle.acquisition_type || "lld",
      });
    }
  }, [vehicle]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/fleet/employees/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.employees || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = { ...form };
      
      // Clean empty values
      Object.keys(payload).forEach(key => {
        if (payload[key] === "" || payload[key] === null) {
          delete payload[key];
        }
      });
      
      // Convert numeric fields
      if (payload.year) payload.year = parseInt(payload.year);
      if (payload.fiscal_power) payload.fiscal_power = parseInt(payload.fiscal_power);
      if (payload.real_power) payload.real_power = parseInt(payload.real_power);
      
      await axios.put(`${API}/fleet/vehicles/${vehicle.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Modifications enregistrées");
      setEditing(false);
      onUpdate?.();
    } catch (err) {
      console.error("Error updating vehicle:", err);
      toast.error("Erreur", {
        description: err.response?.data?.detail || "Impossible de sauvegarder"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm({
      brand: vehicle.brand || "",
      model: vehicle.model || "",
      trim: vehicle.trim || "",
      year: vehicle.year || "",
      registration: vehicle.registration || "",
      vin: vehicle.vin || "",
      first_registration_date: vehicle.first_registration_date || "",
      fuel_type: vehicle.fuel_type || "",
      fiscal_power: vehicle.fiscal_power || "",
      real_power: vehicle.real_power || "",
      color: vehicle.color || "",
      category: vehicle.category || "",
      driver_employee_id: vehicle.driver_employee_id || "",
      assignment: vehicle.assignment || "",
      status: vehicle.status || "active",
      acquisition_type: vehicle.acquisition_type || "lld",
    });
    setEditing(false);
  };

  const InfoRow = ({ label, value }) => (
    <div className="flex justify-between py-2 border-b last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value || "-"}</span>
    </div>
  );

  if (!editing) {
    return (
      <div className="space-y-6">
        <div className="flex justify-end">
          <Button onClick={() => setEditing(true)} data-testid="edit-general-info">
            <Edit2 className="w-4 h-4 mr-2" />
            Modifier
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Identification */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Car className="w-4 h-4 text-indigo-600" />
                Identification
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoRow label="Marque" value={vehicle.brand} />
              <InfoRow label="Modèle" value={vehicle.model} />
              <InfoRow label="Finition" value={vehicle.trim} />
              <InfoRow label="Année" value={vehicle.year} />
              <InfoRow label="Immatriculation" value={vehicle.registration} />
              <InfoRow label="VIN" value={vehicle.vin} />
              <InfoRow label="1ère mise en circulation" value={vehicle.first_registration_date} />
            </CardContent>
          </Card>
          
          {/* Caractéristiques */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Fuel className="w-4 h-4 text-indigo-600" />
                Caractéristiques
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoRow label="Carburant" value={FUEL_TYPE_LABELS[vehicle.fuel_type]} />
              <InfoRow label="Puissance fiscale" value={vehicle.fiscal_power ? `${vehicle.fiscal_power} CV` : null} />
              <InfoRow label="Puissance réelle" value={vehicle.real_power ? `${vehicle.real_power} ch` : null} />
              <InfoRow label="Couleur" value={vehicle.color} />
              <InfoRow label="Catégorie" value={VEHICLE_CATEGORY_LABELS[vehicle.category]} />
            </CardContent>
          </Card>
          
          {/* Affectation */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="w-4 h-4 text-indigo-600" />
                Affectation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoRow label="Conducteur" value={vehicle.driver_name} />
              <InfoRow label="Service / Site" value={vehicle.assignment} />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Edit Mode
  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleCancel} disabled={loading}>
          <X className="w-4 h-4 mr-2" />
          Annuler
        </Button>
        <Button onClick={handleSave} disabled={loading} data-testid="save-general-info">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Identification */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Car className="w-4 h-4 text-indigo-600" />
              Identification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Marque</Label>
                <Input
                  value={form.brand}
                  onChange={(e) => handleChange("brand", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Modèle</Label>
                <Input
                  value={form.model}
                  onChange={(e) => handleChange("model", e.target.value)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Finition</Label>
                <Input
                  value={form.trim}
                  onChange={(e) => handleChange("trim", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Année</Label>
                <Input
                  type="number"
                  value={form.year}
                  onChange={(e) => handleChange("year", e.target.value)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Immatriculation</Label>
                <Input
                  value={form.registration}
                  onChange={(e) => handleChange("registration", e.target.value.toUpperCase())}
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>VIN</Label>
                <Input
                  value={form.vin}
                  onChange={(e) => handleChange("vin", e.target.value.toUpperCase())}
                  className="font-mono"
                  maxLength={17}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Date 1ère mise en circulation</Label>
              <DatePickerFr
                value={form.first_registration_date}
                onChange={(v) => handleChange("first_registration_date", v)}
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Caractéristiques */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Fuel className="w-4 h-4 text-indigo-600" />
              Caractéristiques
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Carburant</Label>
                <Select value={form.fuel_type} onValueChange={(v) => handleChange("fuel_type", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {FUEL_TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => handleChange("category", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {VEHICLE_CATEGORY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Puissance fiscale (CV)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.fiscal_power}
                  onChange={(e) => handleChange("fiscal_power", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Puissance réelle (ch)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.real_power}
                  onChange={(e) => handleChange("real_power", e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Couleur</Label>
              <Input
                value={form.color}
                onChange={(e) => handleChange("color", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Affectation */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-4 h-4 text-indigo-600" />
              Affectation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Conducteur</Label>
              <Select value={form.driver_employee_id} onValueChange={(v) => handleChange("driver_employee_id", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Aucun</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.matricule})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Service / Site</Label>
              <Input
                value={form.assignment}
                onChange={(e) => handleChange("assignment", e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => handleChange("status", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VEHICLE_STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
